import * as React from 'react';
import { useCallback, useRef } from 'react';

import { AsyncContent } from '~/components/AsyncContent';
import { useLoggedInEmployeeId } from '~/components/Auth';
import { useRefDimensions } from '~/hooks/useRefDimensions';
import {
  MarkTaskActions,
  WorklistPanels,
  WorklistRowMode,
} from '~/scenes/MyWorkspace/components/Widgets/components/MyWorklistsWidget/components/common';
import { CollaborationReadApiClient, People } from '~/services/Http';
import {
  BulkUpdateEmployeeTasksStatusInputDto,
  TaskStatusEnum,
} from '~/services/Http/client.people.generated';

import { useSxStyles } from './useStyles';

type Props = {
  isLoading: boolean;
  failedToLoad: boolean;
  worklistData: People.WorklistDto[];
  mutateMyWorklists: () => void;
};

export const MyWorklistsWidgetComponent: React.FC<Props> = (props: Props) => {
  const sxStyles = useSxStyles();
  const { worklistData, isLoading, failedToLoad, mutateMyWorklists } = props;

  const widgetContentRef = useRef(null);
  const dimensions = useRefDimensions({
    ref: widgetContentRef,
  });

  const loggedInEmployeeId = useLoggedInEmployeeId();

  const getRowMode = useCallback((width: number) => {
    if (width > 450) {
      return WorklistRowMode.Table;
    } else if (width > 300) {
      return WorklistRowMode.SemiItem;
    }
    return WorklistRowMode.Item;
  }, []);

  const rowMode = getRowMode(dimensions.width);

  const [selectedTasks, setSelectedTasks] = React.useState<
    Record<number, boolean>
  >({});

  const isAnyTaskSelected = Object.values(selectedTasks).some(
    (selected) => selected,
  );

  const handleUnselectAll = () => {
    setSelectedTasks({});
  };

  const handleUpdateTasks = React.useCallback(
    async (status: 'SKIPPED' | 'COMPLETED' | 'IN_PROGRESS') => {
      if (!worklistData) return;

      const taskIds = Object.keys(selectedTasks)
        .filter((taskId) => selectedTasks[Number(taskId)])
        .map((taskId) => Number(taskId));

      if (taskIds.length === 0) return;

      const updatePayload = {
        employeeId: 'me',
        body: new BulkUpdateEmployeeTasksStatusInputDto({
          taskStatus:
            status as People.BulkUpdateEmployeeTasksStatusInputDtoTaskStatus,
          taskIds: taskIds,
        }),
      };

      try {
        await CollaborationReadApiClient.updateEmployeeTasks(
          updatePayload.employeeId,
          updatePayload.body,
        ).then(() => {
          mutateMyWorklists();
          setSelectedTasks({});
        });
      } catch (err) {
        console.error('Failed to update tasks:', err);
      }
    },
    [worklistData, selectedTasks, mutateMyWorklists],
  );

  const filteredWorklistData = worklistData.filter((listItem) => {
    if (listItem.pertainingTo?.employeeId !== loggedInEmployeeId) {
      return !listItem.tasks.every(
        (task) =>
          task.status?.code === TaskStatusEnum.SKIPPED ||
          task.status?.code === TaskStatusEnum.COMPLETED,
      );
    }
    return true;
  });

  return (
    <>
      <AsyncContent
        isLoading={isLoading}
        failedToLoad={failedToLoad}
        customSxStyles={{ errorMessage: sxStyles.errorMessage }}
      >
        <MarkTaskActions
          onUnselectAll={handleUnselectAll}
          onUpdateTasks={handleUpdateTasks}
          hasTaskSelection={isAnyTaskSelected}
          rowMode={rowMode}
        />
        <WorklistPanels
          worklistData={filteredWorklistData}
          widgetContentRef={widgetContentRef}
          rowMode={rowMode}
          myWorklists={true}
          setSelectedTasks={setSelectedTasks}
          selectedTasks={selectedTasks}
        />
      </AsyncContent>
    </>
  );
};
